import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import CatalogFilters, { type FilterState } from './components/CatalogFilters';
import CatalogGrid from './components/CatalogGrid';
import QuickViewModal from './components/QuickViewModal';
import CatalogPromoBanner from './components/CatalogPromoBanner';
import CategoryQuickNav from './components/CategoryQuickNav';
import { catalogProducts, catalogFilterOptions, type CatalogProduct } from '@/mocks/catalog';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';

const SORT_OPTIONS = [
  { value: 'popular', label: 'По популярности' },
  { value: 'price_asc', label: 'Цена: по возрастанию' },
  { value: 'price_desc', label: 'Цена: по убыванию' },
  { value: 'rating', label: 'По рейтингу' },
  { value: 'discount', label: 'По размеру скидки' },
  { value: 'name', label: 'По названию' },
];

const DEFAULT_FILTERS: FilterState = {
  sections: [], categories: [], brands: [], manufacturers: [], countries: [], forms: [], activeSubstances: [],
  priceMin: '', priceMax: '', prescription: 'all', inStock: false, hasDiscount: false,
};

const PAGE_SIZE = 12;

const SECTION_SEO: Record<string, { title: string; description: string; keywords: string; ogImage: string }> = {
  'Лекарства': {
    title: 'Лекарства — купить онлайн в аптеке NeonPharma | Доставка 2 часа',
    description: 'Купить лекарства онлайн в интернет-аптеке NeonPharma. Рецептурные и безрецептурные препараты от ведущих производителей. Более 10 000 наименований. Доставка по Москве за 2 часа.',
    keywords: 'купить лекарства онлайн, аптека лекарства, рецептурные препараты, безрецептурные препараты, интернет-аптека',
    ogImage: 'https://public.readdy.ai/ai/img_res/e166f5d305300e197eb939e446592756.jpg',
  },
  'БАДы и витамины': {
    title: 'БАДы и витамины — купить в аптеке NeonPharma | Широкий выбор',
    description: 'Купить витамины, БАДы, омега-3 и минералы в интернет-аптеке NeonPharma. Более 3 500 товаров: мультивитамины, пробиотики, антиоксиданты. Быстрая доставка.',
    keywords: 'купить витамины, БАДы онлайн, омега-3, мультивитамины, пробиотики, минералы аптека',
    ogImage: 'https://readdy.ai/api/search-image?query=vitamins%20supplements%20bottles%20yellow%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-vit&orientation=landscape',
  },
  'Дермакосметика': {
    title: 'Дермакосметика — купить в аптеке NeonPharma | La Roche-Posay, Bioderma',
    description: 'Профессиональная дермакосметика в интернет-аптеке NeonPharma. La Roche-Posay, Bioderma, Avène, Ducray. Уход за лицом, телом и волосами. Доставка 2 часа.',
    keywords: 'дермакосметика купить, La Roche-Posay, Bioderma, уход за лицом аптека, косметика аптека',
    ogImage: 'https://readdy.ai/api/search-image?query=dermocosmetics%20skincare%20cream%20serum%20pink%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-derm&orientation=landscape',
  },
  'Гомеопатия': {
    title: 'Гомеопатия — купить гомеопатические препараты | NeonPharma',
    description: 'Гомеопатические препараты в интернет-аптеке NeonPharma. Натуральные средства для иммунитета, нервной системы и суставов. Boiron, Heel, Материа Медика.',
    keywords: 'гомеопатия купить, гомеопатические препараты, Boiron, Heel, натуральные средства аптека',
    ogImage: 'https://readdy.ai/api/search-image?query=homeopathy%20granules%20natural%20medicine%20green%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-homeo&orientation=landscape',
  },
  'Контактные линзы': {
    title: 'Контактные линзы — купить онлайн | Acuvue, Air Optix | NeonPharma',
    description: 'Купить контактные линзы в интернет-аптеке NeonPharma. Acuvue, Air Optix, Bausch+Lomb. Однодневные и месячные линзы, растворы. Доставка по Москве.',
    keywords: 'купить контактные линзы, Acuvue, Air Optix, однодневные линзы, месячные линзы, раствор для линз',
    ogImage: 'https://readdy.ai/api/search-image?query=contact%20lenses%20boxes%20cyan%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-lens&orientation=landscape',
  },
  'Медицинские изделия': {
    title: 'Медицинские изделия — тонометры, термометры, бинты | NeonPharma',
    description: 'Медицинские изделия в интернет-аптеке NeonPharma. Тонометры Omron, термометры Microlife, перевязочные материалы, средства защиты. Более 4 000 товаров.',
    keywords: 'медицинские изделия купить, тонометр Omron, термометр, перевязочные материалы, средства защиты аптека',
    ogImage: 'https://readdy.ai/api/search-image?query=medical%20devices%20blood%20pressure%20monitor%20bandages%20orange%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-mdev&orientation=landscape',
  },
  'Медицинские приборы': {
    title: 'Медицинские приборы — небулайзеры, глюкометры | NeonPharma',
    description: 'Медицинские приборы в интернет-аптеке NeonPharma. Небулайзеры Omron, глюкометры Accu-Chek, пульсоксиметры, массажёры Beurer. Гарантия и доставка.',
    keywords: 'небулайзер купить, глюкометр, пульсоксиметр, массажёр медицинский, ингалятор аптека',
    ogImage: 'https://readdy.ai/api/search-image?query=medical%20equipment%20nebulizer%20glucometer%20violet%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-equip&orientation=landscape',
  },
};

const DEFAULT_SEO = {
  title: 'Каталог — лекарства, витамины, дермакосметика | NeonPharma',
  description: 'Каталог интернет-аптеки NeonPharma: лекарства, БАДы и витамины, дермакосметика, гомеопатия, контактные линзы, медицинские изделия и приборы. Доставка за 2 часа.',
  keywords: 'каталог лекарств, купить витамины, дермакосметика, медицинские приборы, аптека онлайн',
  ogImage: 'https://readdy.ai/api/search-image?query=pharmacy%20catalog%20medicine%20vitamins%20pink%20neon%20glow%20dark%20background%20cyberpunk%20minimal&width=1200&height=630&seq=og-catalog&orientation=landscape',
};

const buildJsonLd = (activeSection: string, filteredCount: number) => {
  const base = `${SITE_URL}/catalog`;
  const url = activeSection ? `${base}?section=${encodeURIComponent(activeSection)}` : base;
  const name = activeSection ? `${activeSection} — NeonPharma` : 'Каталог лекарств — NeonPharma';

  const breadcrumbs: object[] = [
    { '@type': 'ListItem', position: 1, name: 'Главная', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: 'Каталог', item: base },
  ];
  if (activeSection) {
    breadcrumbs.push({ '@type': 'ListItem', position: 3, name: activeSection, item: url });
  }

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name,
      description: activeSection
        ? (SECTION_SEO[activeSection]?.description ?? DEFAULT_SEO.description)
        : DEFAULT_SEO.description,
      url,
      numberOfItems: filteredCount,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs,
    },
  ];
};

const CatalogPage = () => {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [activeSection, setActiveSection] = useState('');
  const [sort, setSort] = useState('popular');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<CatalogProduct | null>(null);

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: toolbarRef, isVisible: toolbarVisible } = useScrollReveal({ threshold: 0.1 });

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { setPage(1); }, [filters, sort, search, activeSection]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    c += filters.sections.length + filters.categories.length + filters.brands.length + filters.manufacturers.length;
    c += filters.countries.length + filters.forms.length + filters.activeSubstances.length;
    if (filters.priceMin) c++;
    if (filters.priceMax) c++;
    if (filters.prescription !== 'all') c++;
    if (filters.inStock) c++;
    if (filters.hasDiscount) c++;
    return c;
  }, [filters]);

  const filtered = useMemo(() => {
    let list = [...catalogProducts];

    // Section filter (from quick nav)
    if (activeSection) list = list.filter((p) => p.section === activeSection);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.activeSubstance.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q)
      );
    }
    if (filters.sections.length) list = list.filter((p) => filters.sections.includes(p.section));
    if (filters.categories.length) list = list.filter((p) => filters.categories.includes(p.category));
    if (filters.brands.length) list = list.filter((p) => filters.brands.includes(p.brand));
    if (filters.manufacturers.length) list = list.filter((p) => filters.manufacturers.includes(p.manufacturer));
    if (filters.countries.length) list = list.filter((p) => filters.countries.includes(p.country));
    if (filters.forms.length) list = list.filter((p) => filters.forms.includes(p.form));
    if (filters.activeSubstances.length) list = list.filter((p) => filters.activeSubstances.includes(p.activeSubstance));
    if (filters.priceMin) list = list.filter((p) => p.price >= Number(filters.priceMin));
    if (filters.priceMax) list = list.filter((p) => p.price <= Number(filters.priceMax));
    if (filters.prescription === 'yes') list = list.filter((p) => p.prescription);
    if (filters.prescription === 'no') list = list.filter((p) => !p.prescription);
    if (filters.inStock) list = list.filter((p) => p.inStock);
    if (filters.hasDiscount) list = list.filter((p) => p.oldPrice !== null && p.oldPrice > p.price);

    switch (sort) {
      case 'price_asc': list.sort((a, b) => a.price - b.price); break;
      case 'price_desc': list.sort((a, b) => b.price - a.price); break;
      case 'rating': list.sort((a, b) => b.rating - a.rating); break;
      case 'discount': list.sort((a, b) => b.discount - a.discount); break;
      case 'name': list.sort((a, b) => a.name.localeCompare(b.name, 'ru')); break;
      default: list.sort((a, b) => b.reviews - a.reviews);
    }

    return list;
  }, [filters, sort, search, activeSection]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const currentSort = SORT_OPTIONS.find((o) => o.value === sort)!;

  const handleSectionChange = (sec: string) => {
    setActiveSection(prev => prev === sec ? '' : sec);
    setFilters(f => ({ ...f, categories: [] }));
  };

  const handleCategoryToggle = (cat: string) => {
    setFilters(f => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter(c => c !== cat)
        : [...f.categories, cat],
    }));
  };

  const handleClearAll = () => {
    setActiveSection('');
    setFilters(DEFAULT_FILTERS);
  };

  const pageTitle = activeSection || 'Каталог';

  const seo = activeSection ? (SECTION_SEO[activeSection] ?? DEFAULT_SEO) : DEFAULT_SEO;
  const canonicalPath = activeSection ? `/catalog?section=${encodeURIComponent(activeSection)}` : '/catalog';

  return (
    <div className="min-h-screen" style={{ background: '#050508' }}>
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical={canonicalPath}
        ogImage={seo.ogImage}
        jsonLd={buildJsonLd(activeSection, filtered.length)}
      />
      <Navbar />

      {/* Header */}
      <header
        ref={headerRef as React.RefObject<HTMLElement>}
        className="pt-28 pb-8 px-6 lg:px-12 relative overflow-hidden"
        style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
          transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div className="absolute top-0 right-0 w-96 h-64 opacity-8 rounded-full" style={{ background: '#ff2d78', filter: 'blur(100px)' }} />
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Breadcrumb */}
          <nav aria-label="Хлебные крошки" className="flex items-center gap-2 font-mono text-xs text-white/30 mb-6 flex-wrap">
            <Link to="/" className="hover:text-white/60 transition-colors">Главная</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/60">Каталог</span>
            {activeSection && (
              <>
                <i className="ri-arrow-right-s-line" />
                <span style={{ color: '#ff2d78' }}>{activeSection}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs tracking-[0.3em] text-white/40 mb-1">КАТАЛОГ</p>
              <h1 className="font-orbitron font-black text-3xl md:text-5xl text-white">
                {pageTitle.toUpperCase()}
              </h1>
              <p className="font-mono text-xs text-white/30 mt-2">
                Найдено: <span style={{ color: '#ff2d78' }}>{filtered.length}</span> товаров
              </p>
            </div>

            {/* Search bar */}
            <div className="relative w-full md:w-96">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по названию, веществу, бренду..."
                aria-label="Поиск по каталогу"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-10 py-3 font-mono text-sm text-white placeholder-white/20 outline-none focus:border-white/30 transition-all"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer" aria-label="Очистить поиск">
                  <i className="ri-close-circle-line text-white/30 hover:text-white/60 transition-colors" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div
        ref={toolbarRef as React.RefObject<HTMLDivElement>}
        className="px-6 lg:px-12 pb-4"
        style={{
          opacity: toolbarVisible ? 1 : 0,
          transform: toolbarVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.15s',
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs cursor-pointer transition-all"
              style={{ border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}
            >
              <i className="ri-filter-3-line" />
              Фильтры
              {activeFilterCount > 0 && (
                <span className="w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-bold" style={{ background: '#ff2d78', color: '#050508' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Active chips */}
            <div className="flex flex-wrap gap-2">
              {filters.sections.map((s) => (
                <span key={s} className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                  {s}
                  <button onClick={() => setFilters(f => ({ ...f, sections: f.sections.filter(x => x !== s) }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              ))}
              {filters.categories.map((c) => (
                <span key={c} className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                  {c}
                  <button onClick={() => setFilters(f => ({ ...f, categories: f.categories.filter(x => x !== c) }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              ))}
              {filters.forms.map((f) => (
                <span key={f} className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.3)', color: '#00f5ff' }}>
                  {f}
                  <button onClick={() => setFilters(prev => ({ ...prev, forms: prev.forms.filter(x => x !== f) }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              ))}
              {filters.brands.map((b) => (
                <span key={b} className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }}>
                  {b}
                  <button onClick={() => setFilters(f => ({ ...f, brands: f.brands.filter(x => x !== b) }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              ))}
              {(filters.priceMin || filters.priceMax) && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(240,255,0,0.1)', border: '1px solid rgba(240,255,0,0.3)', color: '#f0ff00' }}>
                  Цена: {filters.priceMin ? `${Number(filters.priceMin).toLocaleString()}₽` : 'от'} — {filters.priceMax ? `${Number(filters.priceMax).toLocaleString()}₽` : 'до'}
                  <button onClick={() => setFilters(f => ({ ...f, priceMin: '', priceMax: '' }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              )}
              {filters.inStock && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }}>
                  В наличии
                  <button onClick={() => setFilters(f => ({ ...f, inStock: false }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              )}
              {filters.hasDiscount && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-[10px]" style={{ background: 'rgba(255,45,120,0.1)', border: '1px solid rgba(255,45,120,0.3)', color: '#ff2d78' }}>
                  Со скидкой
                  <button onClick={() => setFilters(f => ({ ...f, hasDiscount: false }))} className="cursor-pointer ml-0.5"><i className="ri-close-line text-[10px]" /></button>
                </span>
              )}
              {(activeFilterCount > 0 || activeSection) && (
                <button onClick={handleClearAll} className="whitespace-nowrap px-2.5 py-1 rounded-full font-mono text-[10px] cursor-pointer" style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }}>
                  Сбросить всё
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 ml-auto">
            {/* Sort */}
            <div className="relative">
              <button
                onClick={() => setSortOpen((v) => !v)}
                className="whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs cursor-pointer transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
              >
                <i className="ri-sort-desc" />
                {currentSort.label}
                <i className={`text-white/30 transition-transform ${sortOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
              </button>
              {sortOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-52 rounded-xl overflow-hidden z-30"
                  style={{ background: '#0d0d1a', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setSort(opt.value); setSortOpen(false); }}
                      className="w-full text-left px-4 py-2.5 font-mono text-xs cursor-pointer transition-all whitespace-nowrap"
                      style={
                        sort === opt.value
                          ? { color: '#ff2d78', background: 'rgba(255,45,120,0.08)' }
                          : { color: 'rgba(255,255,255,0.5)' }
                      }
                    >
                      {sort === opt.value && <i className="ri-check-line mr-2" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* View mode */}
            <div className="flex gap-1 p-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => setViewMode('grid')}
                className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all"
                style={viewMode === 'grid' ? { background: '#ff2d78', color: '#fff' } : { color: 'rgba(255,255,255,0.3)' }}
              >
                <i className="ri-grid-line text-sm" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all"
                style={viewMode === 'list' ? { background: '#ff2d78', color: '#fff' } : { color: 'rgba(255,255,255,0.3)' }}
              >
                <i className="ri-list-check text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Promo Banner */}
      <div className="px-6 lg:px-12 pb-5">
        <div className="max-w-7xl mx-auto">
          <CatalogPromoBanner />
        </div>
      </div>

      {/* Main content */}
      <main className="px-6 lg:px-12 pb-20">
        <div className="max-w-7xl mx-auto flex gap-6 items-start">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block" aria-label="Фильтры каталога">
            <CatalogFilters filters={filters} onChange={setFilters} onReset={() => { setFilters(DEFAULT_FILTERS); setActiveSection(''); }} activeCount={activeFilterCount} />
          </aside>

          {/* Products */}
          <section className="flex-1 min-w-0" aria-label={activeSection ? `Товары раздела ${activeSection}` : 'Товары каталога'}>
            {/* Category Quick Nav */}
            <CategoryQuickNav
              activeSection={activeSection}
              activeCategories={filters.categories}
              onSectionChange={handleSectionChange}
              onCategoryToggle={handleCategoryToggle}
              onClearCategories={() => setFilters(f => ({ ...f, categories: [] }))}
              onClearAll={handleClearAll}
              totalCount={filtered.length}
            />

            <CatalogGrid products={paginated} viewMode={viewMode} onQuickView={setQuickViewProduct} />

            {/* Pagination */}
            {totalPages > 1 && (
              <nav aria-label="Пагинация каталога" className="mt-10 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Предыдущая страница"
                  className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all disabled:opacity-30"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    aria-label={`Страница ${p}`}
                    aria-current={page === p ? 'page' : undefined}
                    className="w-9 h-9 flex items-center justify-center rounded-full font-mono text-xs cursor-pointer transition-all"
                    style={
                      page === p
                        ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 12px #ff2d7860' }
                        : { border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }
                    }
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Следующая страница"
                  className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all disabled:opacity-30"
                  style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </nav>
            )}
          </section>
        </div>
      </main>

      {/* Mobile filters drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
          <div
            className="absolute right-0 top-0 bottom-0 w-80 overflow-y-auto"
            style={{ background: '#0a0a14', borderLeft: '1px solid rgba(255,45,120,0.2)' }}
          >
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <span className="font-orbitron font-bold text-sm text-white">ФИЛЬТРЫ</span>
              <button onClick={() => setMobileFiltersOpen(false)} className="cursor-pointer">
                <i className="ri-close-line text-xl text-white/50 hover:text-white transition-colors" />
              </button>
            </div>
            <div className="p-4">
              <CatalogFilters 
                filters={filters} 
                onChange={setFilters} 
                onReset={() => { setFilters(DEFAULT_FILTERS); setActiveSection(''); }} 
                activeCount={activeFilterCount}
                onApply={() => setMobileFiltersOpen(false)}
                showApplyButton={true}
              />
            </div>
          </div>
        </div>
      )}

      <Footer />

      <QuickViewModal
        product={quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
      />
    </div>
  );
};

export default CatalogPage;
